import { useCallback, useContext, useRef } from 'react';

import ComposerContext from 'components/composer/contexts/composer_context';
import { isContentEqual, isEditorEmpty } from 'components/text_editor/helpers';
import TypingState from 'models/typing_state';
import UpdateTypingStatus from 'actions/composition/update_typing_status';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import { useDebounced, useThrottled } from 'components/hooks/debounce_hooks';

const START_TYPING_INTERVAL = 5000;
const STOP_TYPING_TIMEOUT = 5500;

// useChatOnChange will fire off debounced typing started and typing stopped events when the
// composer content has changed.
export function useChatOnChange() {
  const { onStartTyping, onStopTyping } = useOnTyping();

  const previousValue = useRef(null);
  return useCallback(
    ({ value }) => {
      if ((!previousValue.current || !isContentEqual(previousValue.current, value)) && !isEditorEmpty(value)) {
        onStartTyping();
        onStopTyping();
      }
      previousValue.current = value;
    },
    [onStartTyping, onStopTyping]
  );
}

export function useOnTyping() {
  const { composition } = useContext(ComposerContext);
  const { customerId, conversationId, content } = composition;
  const { sessionItemId } = content;

  const executeAction = useExecuteAction();
  const onStopTyping = useCallback(
    () =>
      executeAction(UpdateTypingStatus, {
        channel: 'chat',
        typingState: TypingState.STOPPED,
        customerId,
        conversationId,
        itemId: sessionItemId,
      }),
    [executeAction, customerId, conversationId, sessionItemId]
  );
  const debouncedOnStopTyping = useDebounced(onStopTyping, STOP_TYPING_TIMEOUT);
  const onStartTyping = useCallback(() => {
    executeAction(UpdateTypingStatus, {
      channel: 'chat',
      typingState: TypingState.STARTED,
      customerId,
      conversationId,
      itemId: sessionItemId,
    });
    debouncedOnStopTyping();
  }, [executeAction, customerId, conversationId, sessionItemId, debouncedOnStopTyping]);
  const throttledOnStartTyping = useThrottled(onStartTyping, START_TYPING_INTERVAL);

  return { onStartTyping: throttledOnStartTyping, onStopTyping: debouncedOnStopTyping };
}
